import React, { useEffect, useState } from "react";
import { Sparkles, Clock, Ticket, CalendarDays } from "lucide-react";

/** Décompte vivant : se met à jour chaque seconde sans refetch réseau. */
export function useCountdown(endsInSeconds) {
  const [left, setLeft] = useState(
    typeof endsInSeconds === "number" ? endsInSeconds : null
  );
  useEffect(() => {
    setLeft(typeof endsInSeconds === "number" ? endsInSeconds : null);
    if (typeof endsInSeconds !== "number") return undefined;
    const id = setInterval(() => setLeft((v) => (v == null ? v : v - 1)), 1000);
    return () => clearInterval(id);
  }, [endsInSeconds]);
  return left;
}

export function formatCountdown(seconds) {
  if (seconds == null) return null;
  if (seconds <= 0) return "terminée";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d} j ${String(h).padStart(2, "0")} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} min`;
  return `${m} min ${String(s).padStart(2, "0")} s`;
}

export const formatDateFr = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
};

/** Compte à rebours compact réutilisable (cards de plan, page inscription). */
export const PromoCountdown = ({ endsInSeconds, endsAt, className = "", testid }) => {
  const left = useCountdown(endsInSeconds);
  if (left == null) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={testid}>
      <Clock className="h-3 w-3" />
      {left > 0
        ? <>Plus que <b>{formatCountdown(left)}</b>{endsAt && <> · jusqu'au {formatDateFr(endsAt)}</>}</>
        : <>Offre terminée</>}
    </span>
  );
};

/** Bandeau public des offres en cours — affiche la fenêtre de validité et le décompte. */
export const PromoBanner = ({ offers, onCta }) => {
  const items = (offers || []).filter((o) => o.status === "running");
  if (items.length === 0) return null;

  return (
    <div className="mb-8 space-y-3" data-testid="promo-banner">
      {items.map((o) => {
        const left = o.ends_in_seconds;
        const urgent = typeof left === "number" && left > 0 && left < 3 * 86400;
        const remaining = o.max_uses != null ? Math.max(0, o.max_uses - o.uses_count) : null;
        return (
          <div
            key={o.promotion_id}
            data-testid={`promo-banner-${o.promotion_id}`}
            className={`border-2 p-4 lg:p-5 flex flex-wrap items-center gap-4 ${
              urgent ? "border-[#E60000] bg-[#E60000]/10" : "border-[#FFD700] bg-[#FFD700]/10"
            }`}
          >
            <div
              className="font-display font-black text-3xl lg:text-4xl tracking-tighter px-3 py-1 shrink-0"
              style={{ backgroundColor: urgent ? "#E60000" : "#FFD700", color: urgent ? "#fff" : "#0A0A0A" }}
              data-testid={`promo-banner-value-${o.promotion_id}`}
            >
              {o.discount_type === "percent" ? `-${o.discount_value}%` : `-${o.discount_value}€`}
            </div>

            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <Sparkles className={`h-4 w-4 ${urgent ? "text-[#E60000]" : "text-[#FFD700]"}`} />
                <span className="font-display font-bold text-base lg:text-lg">{o.name}</span>
                {o.auto_apply && (
                  <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-[#059669] text-white px-2 py-0.5">
                    Appliquée automatiquement
                  </span>
                )}
                {o.code && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold bg-[#0A0A0A] text-[#FFD700] px-2 py-0.5">
                    <Ticket className="h-3 w-3" />{o.code}
                  </span>
                )}
              </div>
              {o.description && <div className="text-sm text-white/70 mt-1">{o.description}</div>}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-white/60">
                {(o.starts_at || o.ends_at) && (
                  <span className="inline-flex items-center gap-1" data-testid={`promo-banner-window-${o.promotion_id}`}>
                    <CalendarDays className="h-3 w-3" />
                    {o.starts_at && o.ends_at
                      ? <>Du {formatDateFr(o.starts_at)} au {formatDateFr(o.ends_at)}</>
                      : o.ends_at
                        ? <>Valable jusqu'au {formatDateFr(o.ends_at)}</>
                        : <>Depuis le {formatDateFr(o.starts_at)}</>}
                  </span>
                )}
                {!o.ends_at && !o.starts_at && <span>Sans date de fin</span>}
                {remaining != null && (
                  <span data-testid={`promo-banner-remaining-${o.promotion_id}`}>
                    Plus que <b>{remaining}</b> place(s) sur {o.max_uses}
                  </span>
                )}
              </div>
            </div>

            {typeof left === "number" && (
              <div
                className={`shrink-0 px-4 py-2 border font-mono text-sm ${
                  urgent ? "border-[#E60000] text-[#E60000] bg-white" : "border-[#FFD700] text-[#FFD700]"
                }`}
                data-testid={`promo-banner-countdown-${o.promotion_id}`}
              >
                <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">Se termine dans</div>
                <LiveCountdown seconds={left} />
              </div>
            )}

            {onCta && (
              <button
                type="button"
                onClick={onCta}
                data-testid={`promo-banner-cta-${o.promotion_id}`}
                className={`shrink-0 h-11 px-5 font-bold text-xs uppercase tracking-wider transition-colors ${
                  urgent ? "bg-[#E60000] text-white hover:bg-[#B80000]" : "bg-[#FFD700] text-[#0A0A0A] hover:bg-[#E6C200]"
                }`}
              >
                En profiter
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const LiveCountdown = ({ seconds }) => {
  const left = useCountdown(seconds);
  return <div className="font-display font-black text-lg tracking-tight">{formatCountdown(left) || "—"}</div>;
};
